import { create } from 'zustand';

const STORAGE_PREFIX = 'client-portal-react.product-schemas';

const safeWindow = () => (typeof window !== 'undefined' ? window : null);
const requireTenantId = (tenantId) => {
  const normalizedTenantId = `${tenantId ?? ''}`.trim();
  if (!normalizedTenantId || normalizedTenantId === 'unknown-tenant') {
    throw new Error('Tenant is required for schema cache operations.');
  }
  return normalizedTenantId;
};

export const makeSchemaCacheKey = (tenantId, schemaSetVersion) => {
  return `${STORAGE_PREFIX}:${requireTenantId(tenantId)}:${
    schemaSetVersion || 'unknown-version'
  }`;
};

const readPersistedCache = (tenantId, schemaSetVersion) => {
  const browserWindow = safeWindow();
  if (!browserWindow) {
    return null;
  }

  const cacheKey = makeSchemaCacheKey(tenantId, schemaSetVersion);

  try {
    const raw = browserWindow.localStorage.getItem(cacheKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      catalog: Array.isArray(parsed.catalog) ? parsed.catalog : [],
      schemasByCacheKey:
        parsed.schemasByCacheKey && typeof parsed.schemasByCacheKey === 'object'
          ? parsed.schemasByCacheKey
          : {},
      schemaSetVersion: parsed.schemaSetVersion || schemaSetVersion || null,
      tenantId: parsed.tenantId || tenantId || null,
    };
  } catch (error) {
    return null;
  }
};

const persistCurrentCache = (state) => {
  const browserWindow = safeWindow();
  if (!browserWindow) {
    return;
  }

  const { activeTenantId, schemaSetVersion, catalog, schemasByCacheKey } =
    state;
  if (!activeTenantId || !schemaSetVersion) {
    return;
  }

  const cacheKey = makeSchemaCacheKey(activeTenantId, schemaSetVersion);
  const payload = {
    tenantId: activeTenantId,
    schemaSetVersion,
    catalog: Array.isArray(catalog) ? catalog : [],
    schemasByCacheKey:
      schemasByCacheKey && typeof schemasByCacheKey === 'object'
        ? schemasByCacheKey
        : {},
    cachedAt: new Date().toISOString(),
  };

  try {
    browserWindow.localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (error) {
    // Ignore cache write failures; the in-memory store still works.
  }
};

const clearPersistedCache = (tenantId, schemaSetVersion) => {
  const browserWindow = safeWindow();
  if (!browserWindow) {
    return;
  }

  const cacheKey = makeSchemaCacheKey(tenantId, schemaSetVersion);
  try {
    browserWindow.localStorage.removeItem(cacheKey);
  } catch (error) {
    // Ignore cache removal failures.
  }
};

const useProductSchemaStore = create((set, get) => ({
  activeTenantId: null,
  schemaSetVersion: null,
  catalog: [],
  schemasByCacheKey: {},
  isBootstrapping: false,
  isLoadingSchema: false,
  error: null,

  hydrateContext: (tenantId, nextSchemaSetVersion) => {
    const normalizedTenantId = requireTenantId(tenantId);
    const persisted = readPersistedCache(
      normalizedTenantId,
      nextSchemaSetVersion
    );

    set({
      activeTenantId: normalizedTenantId,
      schemaSetVersion: nextSchemaSetVersion || null,
      catalog: persisted?.catalog || [],
      schemasByCacheKey: persisted?.schemasByCacheKey || {},
      error: null,
    });
  },

  setBootstrapState: (isBootstrapping) => {
    set({ isBootstrapping: !!isBootstrapping });
  },

  setLoadingSchema: (isLoadingSchema) => {
    set({ isLoadingSchema: !!isLoadingSchema });
  },

  setError: (error) => {
    set({ error: error || null });
  },

  setCatalog: (catalog) => {
    set({ catalog: Array.isArray(catalog) ? catalog : [] });
    persistCurrentCache(get());
  },

  upsertSchema: (schemaCacheKey, schema) => {
    set((state) => ({
      schemasByCacheKey: {
        ...state.schemasByCacheKey,
        [schemaCacheKey]: schema,
      },
    }));
    persistCurrentCache(get());
  },

  getSchemaByCacheKey: (schemaCacheKey) => {
    return get().schemasByCacheKey[schemaCacheKey] || null;
  },

  getCatalogByProductType: (productType) => {
    return (
      get().catalog.find((entry) => entry?.productType === productType) || null
    );
  },

  resetCurrentCache: () => {
    const { activeTenantId, schemaSetVersion } = get();
    if (activeTenantId && schemaSetVersion) {
      clearPersistedCache(activeTenantId, schemaSetVersion);
    }

    set({
      catalog: [],
      schemasByCacheKey: {},
      error: null,
      isLoadingSchema: false,
      isBootstrapping: false,
    });
  },
}));

export const getSchemaCacheKey = (
  productType,
  sourceCurrency,
  targetCurrency
) => {
  return [
    productType || 'unknown-product',
    sourceCurrency || 'unknown-source',
    targetCurrency || 'unknown-target',
  ].join(':');
};

export const readSchemaCache = (tenantId, schemaSetVersion, schemaCacheKey) => {
  const persisted = readPersistedCache(tenantId, schemaSetVersion);
  if (!persisted) {
    return null;
  }

  return persisted.schemasByCacheKey[schemaCacheKey] || null;
};

export const writeSchemaCache = (
  tenantId,
  schemaSetVersion,
  catalog,
  schemaCacheKey,
  schema
) => {
  const browserWindow = safeWindow();
  if (!browserWindow || !tenantId || !schemaSetVersion) {
    return;
  }

  const cacheKey = makeSchemaCacheKey(tenantId, schemaSetVersion);
  const current = readPersistedCache(tenantId, schemaSetVersion) || {
    tenantId: requireTenantId(tenantId),
    schemaSetVersion,
    catalog: [],
    schemasByCacheKey: {},
  };

  const payload = {
    tenantId: requireTenantId(tenantId),
    schemaSetVersion,
    catalog: Array.isArray(catalog) ? catalog : current.catalog || [],
    schemasByCacheKey: {
      ...(current.schemasByCacheKey || {}),
      [schemaCacheKey]: schema,
    },
    cachedAt: new Date().toISOString(),
  };

  try {
    browserWindow.localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (error) {
    // Ignore cache write failures; the in-memory store still works.
  }
};

export default useProductSchemaStore;
